import React from 'react';
import {
    Badge,
    Box,
    Button,
    Card,
    Flex,
    Heading,
    HStack,
    Icon,
    Progress,
    SimpleGrid,
    Text,
    VStack,
} from '@chakra-ui/react';
import { FiArrowRight, FiDownload, FiRefreshCw } from 'react-icons/fi';

export const WorkspaceCard = ({ children, p = 5, dark = false, ...props }) => (
    <Card
        bg={dark ? 'brand.navy' : 'white'}
        color={dark ? 'white' : 'brand.ink'}
        borderWidth={dark ? 0 : '1px'}
        borderColor="brand.divider"
        borderRadius="14px"
        boxShadow={dark ? '0 14px 36px rgba(11, 37, 69, 0.14)' : 'b2bCard'}
        overflow="hidden"
        {...props}
    >
        <Box p={p}>{children}</Box>
    </Card>
);

export const Eyebrow = ({ children, color = 'brand.tealDark', ...props }) => (
    <Text
        fontSize="10.5px"
        fontWeight="800"
        letterSpacing="0.12em"
        textTransform="uppercase"
        color={color}
        {...props}
    >
        {children}
    </Text>
);

export const KpiTile = ({ eyebrow, value, sub, icon, tone = 'default', delta }) => {
    const dark = tone === 'navy';
    const teal = tone === 'teal';

    return (
        <Box
            bg={dark ? 'brand.navy' : teal ? 'brand.tealSoft' : 'white'}
            color={dark ? 'white' : 'brand.ink'}
            borderWidth={dark || teal ? 0 : '1px'}
            borderColor="brand.divider"
            borderRadius="14px"
            p={4}
            minH="106px"
            boxShadow={dark ? 'none' : 'b2bCard'}
        >
            <HStack justify="space-between" align="center">
                <Eyebrow color={dark ? '#5eead4' : teal ? 'brand.tealDark' : 'brand.subtle'}>
                    {eyebrow}
                </Eyebrow>
                {icon ? (
                    <Icon as={icon} color={dark ? '#5eead4' : 'brand.teal'} boxSize={4} />
                ) : null}
            </HStack>
            <Text
                mt={2}
                fontSize={{ base: '24px', lg: '28px' }}
                lineHeight="1"
                fontWeight="900"
                letterSpacing="0"
                sx={{ fontVariantNumeric: 'tabular-nums' }}
            >
                {typeof value === 'number' ? value.toLocaleString('en-US') : value}
            </Text>
            <HStack mt={2} spacing={2} color={dark ? 'whiteAlpha.700' : 'brand.subtle'} fontSize="12px">
                {delta !== undefined ? (
                    <Text color={Number(delta) >= 0 ? 'green.500' : 'red.500'} fontWeight="800">
                        {Number(delta) >= 0 ? '+' : ''}{delta}%
                    </Text>
                ) : null}
                <Text>{sub}</Text>
            </HStack>
        </Box>
    );
};

export const WorkspaceHero = ({
    eyebrow,
    title,
    accent,
    description,
    primaryAction,
    secondaryAction,
    kpis = [],
}) => (
    <WorkspaceCard dark p={{ base: 5, lg: 6 }}>
        <SimpleGrid columns={{ base: 1, xl: kpis.length ? 2 : 1 }} spacing={6} alignItems="center">
            <Box>
                <Eyebrow color="#5eead4">{eyebrow}</Eyebrow>
                <Heading
                    mt={2}
                    color="white"
                    fontSize={{ base: '24px', md: '28px' }}
                    lineHeight="1.15"
                    letterSpacing="0"
                >
                    {title}{' '}
                    {accent ? <Box as="span" color="#5eead4">{accent}</Box> : null}
                </Heading>
                <Text mt={3} color="whiteAlpha.700" maxW="680px" fontSize="14px" lineHeight="1.6">
                    {description}
                </Text>
                {(primaryAction || secondaryAction) ? (
                    <HStack mt={5} spacing={3} flexWrap="wrap">
                        {primaryAction ? (
                            <Button
                                leftIcon={primaryAction.icon ? <Icon as={primaryAction.icon} /> : undefined}
                                rightIcon={primaryAction.rightIcon === false ? undefined : <FiArrowRight />}
                                bg="brand.teal"
                                color="brand.navy"
                                _hover={{ bg: '#5eead4' }}
                                onClick={primaryAction.onClick}
                            >
                                {primaryAction.label}
                            </Button>
                        ) : null}
                        {secondaryAction ? (
                            <Button
                                leftIcon={<Icon as={secondaryAction.icon || FiRefreshCw} />}
                                variant="ghost"
                                bg="whiteAlpha.100"
                                color="white"
                                _hover={{ bg: 'whiteAlpha.200' }}
                                onClick={secondaryAction.onClick}
                            >
                                {secondaryAction.label}
                            </Button>
                        ) : null}
                    </HStack>
                ) : null}
            </Box>
            {kpis.length ? (
                <SimpleGrid columns={{ base: 2, md: 4, xl: 2 }} spacing={3}>
                    {kpis.map((item) => (
                        <KpiTile key={item.eyebrow} tone="navy" {...item} />
                    ))}
                </SimpleGrid>
            ) : null}
        </SimpleGrid>
    </WorkspaceCard>
);

export const ActionList = ({ title, subtitle, actions = [] }) => (
    <WorkspaceCard p={0}>
        <Flex
            justify="space-between"
            align="center"
            gap={4}
            px={5}
            py={4}
            borderBottomWidth="1px"
            borderColor="brand.divider"
        >
            <Box>
                <Heading size="sm" color="brand.ink">{title}</Heading>
                {subtitle ? <Text mt={1} fontSize="12px" color="brand.subtle">{subtitle}</Text> : null}
            </Box>
            <Button size="sm" variant="ghost" rightIcon={<FiArrowRight />}>See all</Button>
        </Flex>
        <VStack spacing={0} align="stretch">
            {actions.map((action, index) => (
                <Flex
                    key={`${action.title}-${index}`}
                    gap={4}
                    align="flex-start"
                    px={5}
                    py={4}
                    borderBottomWidth={index < actions.length - 1 ? '1px' : 0}
                    borderColor="brand.divider"
                    bg={index === 0 ? 'brand.tealSoft' : 'white'}
                >
                    <Box
                        w="38px"
                        h="38px"
                        borderRadius="10px"
                        bg="white"
                        color="brand.tealDark"
                        display="grid"
                        placeItems="center"
                        flexShrink={0}
                        borderWidth="1px"
                        borderColor="brand.divider"
                    >
                        <Icon as={action.icon || FiArrowRight} boxSize={4} />
                    </Box>
                    <Box flex="1" minW={0}>
                        <HStack spacing={2} mb={1} flexWrap="wrap">
                            <Badge bg={action.toneBg || 'brand.tealSoft'} color={action.toneColor || 'brand.tealDark'}>
                                {action.badge}
                            </Badge>
                            {action.time ? (
                                <Text fontSize="11px" color="brand.subtle" fontFamily="mono">{action.time}</Text>
                            ) : null}
                        </HStack>
                        <Text fontWeight="800" color="brand.ink">{action.title}</Text>
                        <Text mt={1} fontSize="12px" color="brand.muted" lineHeight="1.5">
                            {action.description}
                        </Text>
                    </Box>
                    {action.cta ? (
                        <Button size="sm" variant="outline" rightIcon={<FiArrowRight />} onClick={action.onClick}>
                            {action.cta}
                        </Button>
                    ) : null}
                </Flex>
            ))}
        </VStack>
    </WorkspaceCard>
);

export const ProgressMetric = ({ label, value, helper, color = 'brand.teal' }) => (
    <Box>
        <HStack justify="space-between" fontSize="12px" mb={2}>
            <Text color="brand.ink" fontWeight="700">{label}</Text>
            <Text color="brand.ink" fontWeight="900" sx={{ fontVariantNumeric: 'tabular-nums' }}>{value}%</Text>
        </HStack>
        <Progress value={value} colorScheme={color === 'orange.400' ? 'orange' : 'teal'} borderRadius="full" h="7px" />
        {helper ? <Text mt={1.5} fontSize="11px" color="brand.subtle">{helper}</Text> : null}
    </Box>
);

export const ExportButton = ({ children = 'Export PDF / CSV', ...props }) => (
    <Button size="sm" variant="outline" leftIcon={<FiDownload />} {...props}>
        {children}
    </Button>
);
